import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { AlertController, ToastController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { SentryService } from './sentry.service';
import { NewEvaluationService } from './new-evaluation.service';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';

export interface CacheStats {
  totalCaches: number;
  cacheNames: string[];
  totalSize: number;
  entries: { [key: string]: number };
}

@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService {
  private pendingUpdate: VersionReadyEvent | null = null;

  constructor(
    private swUpdate: SwUpdate,
    private alertController: AlertController,
    private appRef: ApplicationRef,
    private sentryService: SentryService,
    private toastController: ToastController,
    private newEvaluationService: NewEvaluationService,
  ) { }

  /**
   * Inicializa el servicio de actualizaciones PWA
   * Verifica actualizaciones cada 6 horas y cuando la app se estabiliza
   */
  init(): void {
    // En app nativa (Android/iOS vía Capacitor), las actualizaciones son por Play Store / App Store
    // El Service Worker no debe interferir
    if (Capacitor.getPlatform() !== 'web') {
      console.log('📱 App nativa — actualizaciones gestionadas por la tienda');
      return;
    }

    if (!this.swUpdate.isEnabled) {
      console.log('⚠️ Service Worker no está habilitado');
      return;
    }

    // Verificar actualizaciones cuando la app se estabiliza
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    const everyThirtyMinutes$ = interval(30 * 60 * 1000); // 30 minutos
    const everyThirtyMinutesOnceAppIsStable$ = concat(appIsStable$, everyThirtyMinutes$);

    everyThirtyMinutesOnceAppIsStable$.subscribe(async () => {
      try {
        console.log('🔄 Verificando actualizaciones de PWA...');
        const updateFound = await this.swUpdate.checkForUpdate();
        if (updateFound) {
          console.log('✅ Nueva versión disponible');
        } else {
          console.log('✅ App actualizada');
        }
      } catch (err) {
        console.error('❌ Error verificando actualizaciones:', err);
        this.sentryService.captureException(err, {
          tags: { feature: 'pwa-update' },
          extra: { action: 'checkForUpdate' }
        });
      }
    });

    // Escuchar cuando hay una nueva versión disponible
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(evt => {
        console.log('🆕 Nueva versión detectada:', evt.latestVersion);
        this.promptUser(evt);
      });

    // Manejar errores de actualización
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('❌ Error irrecuperable del Service Worker:', event.reason);
      this.handleUnrecoverableError(event.reason);
    });

    // Re-verificar actualizaciones cuando el documento vuelve a ser visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.swUpdate.checkForUpdate().catch((err) => {
          console.error('❌ Error verificando actualizaciones (visibility):', err);
        });
      }
    });
  }

  /**
   * Muestra toast no intrusivo al usuario avisando de nueva versión
   * El reload diferido se aplica al navegar a /home o /login
   */
  private async promptUser(evt: VersionReadyEvent): Promise<void> {
    const newVersion = evt.latestVersion.appData?.['version'] || 'nueva';

    // Guardar actualización pendiente para aplicar al llegar a /home
    this.pendingUpdate = evt;

    // Toast no intrusivo — no bloquea al promotor en medio de una evaluación
    const toast = await this.toastController.create({
      message: `🆕 Nueva versión (${newVersion}). Se aplicará al volver al inicio.`,
      duration: 6000,
      position: 'bottom',
      buttons: [
        {
          text: 'Actualizar ahora',
          handler: () => this.activateUpdate()
        }
      ]
    });

    await toast.present();

    // Enviar telemetría a Sentry
    this.sentryService.setContext('pwa_update', {
      currentVersion: evt.currentVersion.appData?.['version'] || 'desconocida',
      newVersion,
      updateAvailable: true,
      updateType: 'deferred'
    });
  }

  /**
   * Activa la actualización y recarga la app
   */
  private async activateUpdate(): Promise<void> {
    try {
      // Forzar persistencia de evaluaciones ANTES de matar la app
      // Así eliminamos la ventana de 300ms del debounce
      await this.newEvaluationService.forcePersist();

      await this.swUpdate.activateUpdate();
      console.log('✅ Actualización activada, recargando...');

      // Mostrar loading antes de recargar
      const alert = await this.alertController.create({
        header: 'Actualizando...',
        message: 'La aplicación se está actualizando',
        backdropDismiss: false
      });

      await alert.present();

      this.pendingUpdate = null;

      // Recargar después de 1 segundo
      setTimeout(() => {
        document.location.reload();
      }, 1000);
    } catch (err) {
      console.error('❌ Error activando actualización:', err);
      this.sentryService.captureException(err, {
        tags: { feature: 'pwa-update' },
        extra: { action: 'activateUpdate' }
      });
    }
  }

  /**
   * Aplica la actualización pendiente si el usuario está en una página segura
   * (home o login, donde no hay formularios activos que puedan romperse).
   * Llamado desde AppComponent en cada NavigationEnd del router.
   */
  async applyUpdateIfAtSafePage(url: string): Promise<void> {
    if (!this.pendingUpdate) return;

    if (url.startsWith('/home') || url.startsWith('/login')) {
      console.log('🔄 Aplicando actualización en página segura:', url);
      await this.activateUpdate();
    }
  }

  /**
   * Apaga la PWA de forma definitiva (solo web).
   * 1. Desregistra todos los Service Workers.
   * 2. Borra todo el Cache Storage (assets precacheados + dataGroups).
   * 3. Muestra la pantalla de cierre con enlaces a las tiendas.
   * Tras esto, la siguiente visita irá directo a Vercel (503) sin SW ni caché.
   */
  async killSwitch(): Promise<void> {
    // 1. Desregistrar Service Workers
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
      console.log('🧹 Service Workers desregistrados');
    } catch (e) {
      console.warn('⚠️ No se pudieron desregistrar los SW:', e);
    }

    // 2. Borrar todo el Cache Storage
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      console.log('🧹 Cachés eliminados:', cacheNames);
    } catch (e) {
      console.warn('⚠️ No se pudieron borrar las cachés:', e);
    }

    // 3. Mostrar pantalla de cierre
    const shutdown = document.getElementById('app-shutdown');
    if (shutdown) {
      shutdown.style.display = 'flex';
    }
  }

  /**
   * Maneja errores irrecuperables del Service Worker
   * Desregistra el SW corrupto y recarga automáticamente
   */
  private async handleUnrecoverableError(reason: string): Promise<void> {
    // Reportar a Sentry ANTES de cualquier acción de recuperación,
    // para garantizar que el error se registre incluso si el reload falla
    this.sentryService.captureException(new Error(`Service Worker unrecoverable: ${reason}`), {
      tags: { feature: 'pwa-update' },
      extra: { reason }
    });

    // Intentar desregistrar el SW corrupto para que no sobreviva al reload
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
      console.log('🧹 Service Worker corrupto desregistrado, recargando...');
    } catch (e) {
      console.warn('⚠️ No se pudo desregistrar el Service Worker:', e);
    }

    // Recargar automáticamente — no pedimos confirmación al usuario
    // porque un SW unrecoverable deja la app en estado inconsistente
    setTimeout(() => {
      document.location.reload();
    }, 1500);

    // Mostrar alert informativo mientras se recarga
    const alert = await this.alertController.create({
      header: 'Recargando aplicación',
      message: 'Se detectó un error en la caché. La aplicación se está recargando...',
      backdropDismiss: false
    });

    await alert.present();
  }

  /**
   * Fuerza la verificación de actualizaciones (para uso manual)
   */
  async checkForUpdates(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      console.log('⚠️ Service Worker no está habilitado');
      return false;
    }

    try {
      console.log('🔄 Verificando actualizaciones manualmente...');
      const updateFound = await this.swUpdate.checkForUpdate();

      if (!updateFound) {
        const alert = await this.alertController.create({
          header: 'App actualizada',
          message: 'Ya tienes la última versión instalada.',
          buttons: ['OK']
        });
        await alert.present();
      }

      return updateFound;
    } catch (err) {
      console.error('❌ Error verificando actualizaciones:', err);

      const alert = await this.alertController.create({
        header: 'Error',
        message: 'No se pudo verificar actualizaciones. Intenta más tarde.',
        buttons: ['OK']
      });
      await alert.present();

      return false;
    }
  }

  /**
   * Limpia la caché del Service Worker (útil para debugging)
   */
  async clearCache(): Promise<void> {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('✅ Caché limpiada');

      const alert = await this.alertController.create({
        header: 'Caché limpiada',
        message: 'La aplicación se recargará con la última versión.',
        buttons: [
          {
            text: 'OK',
            handler: () => {
              document.location.reload();
            }
          }
        ],
        backdropDismiss: false
      });

      await alert.present();
    } catch (err) {
      console.error('❌ Error limpiando caché:', err);
    }
  }

  /**
   * Limpia solo cachés de API, mantiene assets estáticos
   */
  async clearApiCache(): Promise<void> {
    try {
      const cacheNames = await caches.keys();
      const apiCaches = cacheNames.filter(name =>
        name.includes('api-') || name.includes('data')
      );

      await Promise.all(
        apiCaches.map(cacheName => caches.delete(cacheName))
      );

      console.log('✅ Caché de API limpiada:', apiCaches);

      this.sentryService.addBreadcrumb({
        category: 'cache',
        message: 'API cache cleared',
        level: 'info',
        data: { caches: apiCaches }
      });
    } catch (err) {
      console.error('❌ Error limpiando caché de API:', err);
      this.sentryService.captureException(err, {
        tags: { feature: 'pwa-cache' },
        extra: { action: 'clearApiCache' }
      });
    }
  }

  /**
   * Obtiene estadísticas de caché
   */
  async getCacheStats(): Promise<CacheStats> {
    try {
      const cacheNames = await caches.keys();
      const stats: CacheStats = {
        totalCaches: cacheNames.length,
        cacheNames,
        totalSize: 0,
        entries: {}
      };

      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
        stats.entries[cacheName] = keys.length;
      }

      console.log('📊 Estadísticas de caché:', stats);
      return stats;
    } catch (err) {
      console.error('❌ Error obteniendo estadísticas:', err);
      return {
        totalCaches: 0,
        cacheNames: [],
        totalSize: 0,
        entries: {}
      };
    }
  }

  /**
   * Muestra estadísticas de caché al usuario
   */
  async showCacheStats(): Promise<void> {
    const stats = await this.getCacheStats();

    let message = `Cachés activos: ${stats.totalCaches}\n\n`;
    for (const [name, count] of Object.entries(stats.entries)) {
      message += `${name}: ${count} entradas\n`;
    }

    const alert = await this.alertController.create({
      header: 'Estadísticas de Caché',
      message,
      buttons: ['OK']
    });

    await alert.present();
  }

  /**
   * Precarga recursos críticos en caché
   */
  async precacheResources(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      console.log('⚠️ Service Worker no habilitado, no se puede precargar');
      return;
    }

    try {
      console.log('🔄 Precargando recursos críticos...');

      // El Service Worker manejará el precache automáticamente
      // basado en la configuración de ngsw-config.json

      const stats = await this.getCacheStats();
      console.log('✅ Recursos precargados:', stats.totalCaches, 'cachés');

      this.sentryService.addBreadcrumb({
        category: 'cache',
        message: 'Resources precached',
        level: 'info',
        data: stats
      });
    } catch (err) {
      console.error('❌ Error precargando recursos:', err);
      this.sentryService.captureException(err, {
        tags: { feature: 'pwa-cache' },
        extra: { action: 'precacheResources' }
      });
    }
  }

  /**
   * Limpia cachés antiguos (más de 30 días)
   */
  async cleanOldCaches(): Promise<void> {
    try {
      const cacheNames = await caches.keys();
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);

      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();

        for (const request of requests) {
          const response = await cache.match(request);
          if (response) {
            const dateHeader = response.headers.get('date');
            if (dateHeader) {
              const cacheDate = new Date(dateHeader).getTime();
              if (cacheDate < thirtyDaysAgo) {
                await cache.delete(request);
                console.log('🗑️ Entrada antigua eliminada:', request.url);
              }
            }
          }
        }
      }

      console.log('✅ Cachés antiguos limpiados');
    } catch (err) {
      console.error('❌ Error limpiando cachés antiguos:', err);
    }
  }
}
